import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Field, Header, EmptyState } from "@/pages/Vitals";
import { VoiceMicButton } from "@/components/VoiceInput";
import { Plus, Sparkles, Trash2, Send, Bot, User as UserIcon } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function Symptoms() {
  const { activeId, active } = useMembers();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: today(), severity: "mild" });
  const [loading, setLoading] = useState(false);

  // AI chat
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const load = useCallback(() => {
    if (!activeId) return;
    api.get(`/symptoms?member_id=${activeId}`).then(r => setItems(r.data));
  }, [activeId]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.symptom) { toast.error("Enter symptom"); return; }
    if (!activeId) { toast.error("Select a member first"); return; }
    setLoading(true);
    try {
      await api.post("/symptoms", { ...form, member_id: activeId });
      toast.success("Symptom logged");
      setOpen(false); setForm({ date: today(), severity: "mild" }); load();
    } finally { setLoading(false); }
  };
  const del = async (id) => { await api.delete(`/symptoms/${id}`); toast.success("Deleted"); load(); };

  const sendAi = async () => {
    if (!query.trim() || aiLoading) return;
    const userMsg = { id: crypto.randomUUID(), role: "user", text: query };
    setMessages(m => [...m, userMsg]); setQuery(""); setAiLoading(true);
    try {
      const r = await api.post("/ai/symptom-search", { query: userMsg.text, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { id: crypto.randomUUID(), role: "ai", text: r.data.reply }]);
    } catch {
      setMessages(m => [...m, { id: crypto.randomUUID(), role: "ai", text: "Sorry, I couldn't reach the assistant. Try again." }]);
    } finally { setAiLoading(false); }
  };

  return (
    <div className="space-y-6">
      <Header title="Symptoms" subtitle={`For ${active?.name || "—"}. Log symptoms and explore with an AI assistant.`}
        action={
          <div className="flex gap-2">
            <Button data-testid="open-ai-chat-btn" variant="outline" onClick={() => setChatOpen(true)} className="rounded-xl border-[#E5E7E4]">
              <Sparkles className="w-4 h-4 mr-2 text-[#4A6750]" /> Ask AI
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-symptom-btn" className="rounded-xl bg-[#4A6750] hover:bg-[#3B5340]"><Plus className="w-4 h-4 mr-2" /> Add Symptom</Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl max-w-lg">
                <DialogHeader><DialogTitle className="font-heading">Log Symptom</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4" data-testid="symptom-form">
                  <Field label="Date"><Input data-testid="symptom-date" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></Field>
                  <Field label="Symptom"><div className="flex gap-2"><Input data-testid="symptom-name" placeholder="e.g. Headache" value={form.symptom || ""} onChange={e => setForm({...form, symptom: e.target.value})} required className="bg-[#F9F8F6] rounded-xl" /><VoiceMicButton onText={(t) => setForm(f => ({...f, symptom: f.symptom ? `${f.symptom} ${t}` : t}))} /></div></Field>
                  <Field label="Severity">
                    <Select value={form.severity} onValueChange={v => setForm({...form, severity: v})}>
                      <SelectTrigger data-testid="symptom-severity"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mild">Mild</SelectItem>
                        <SelectItem value="moderate">Moderate</SelectItem>
                        <SelectItem value="severe">Severe</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Duration"><Input data-testid="symptom-duration" placeholder="e.g. 2 hours" value={form.duration || ""} onChange={e => setForm({...form, duration: e.target.value})} /></Field>
                  <Field label="Notes"><div className="flex gap-2"><Textarea data-testid="symptom-notes" rows={2} value={form.notes || ""} onChange={e => setForm({...form, notes: e.target.value})} className="bg-[#F9F8F6] rounded-xl flex-1" /><VoiceMicButton onText={(t) => setForm(f => ({...f, notes: f.notes ? `${f.notes} ${t}` : t}))} /></div></Field>
                  <Button type="submit" disabled={loading} data-testid="save-symptom-btn" className="w-full rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">Save</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={Sparkles} title="No symptoms logged" subtitle="Tap 'Add Symptom' to start tracking." />
      ) : (
        <div className="space-y-3">
          {items.map(s => (
            <Card key={s.id} className="p-5 rounded-2xl border border-[#E5E7E4] flex items-start justify-between" data-testid={`symptom-${s.id}`}>
              <div>
                <p className="text-xs font-semibold tracking-[0.1em] uppercase text-[#7D8F84]">{s.date} · {s.severity}</p>
                <h3 className="font-heading text-lg font-medium mt-1">{s.symptom}</h3>
                {s.duration && <p className="text-sm text-[#5C6D63] mt-1">Duration: {s.duration}</p>}
                {s.notes && <p className="text-sm text-[#5C6D63] mt-1 italic">{s.notes}</p>}
              </div>
              <button data-testid={`del-symptom-${s.id}`} onClick={() => del(s.id)} className="text-[#7D8F84] hover:text-[#C76B58] p-1.5"><Trash2 className="w-4 h-4" /></button>
            </Card>
          ))}
        </div>
      )}

      {/* AI chat dialog */}
      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[80vh] flex flex-col p-0" data-testid="ai-chat-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#E5E7E4]">
            <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#4A6750]" /> AI Symptom Assistant</DialogTitle>
            <p className="text-sm text-[#5C6D63]">Ask about possible causes & self-care. Not medical advice.</p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-[300px] max-h-[55vh]">
            {messages.length === 0 && (
              <div className="text-sm text-[#7D8F84] italic">Try: "I have a throbbing headache and nausea since morning."</div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === "user" ? "bg-[#4A6750] text-white" : "bg-[#E8ECE9] text-[#4A6750]"}`}>
                  {m.role === "user" ? <UserIcon className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#E8ECE9] text-[#1C2E24]" : "bg-white border border-[#E5E7E4] text-[#1C2E24]"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {aiLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#E8ECE9] text-[#4A6750] flex items-center justify-center"><Bot className="w-4 h-4" /></div>
                <div className="bg-white border border-[#E5E7E4] rounded-2xl px-4 py-3 text-sm text-[#7D8F84] italic">Thinking…</div>
              </div>
            )}
          </div>
          <div className="border-t border-[#E5E7E4] px-6 py-4 flex gap-2">
            <Input data-testid="ai-input" value={query} onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendAi())}
              placeholder="Describe your symptoms…" className="rounded-xl bg-[#F9F8F6]" />
            <Button data-testid="ai-send-btn" onClick={sendAi} disabled={aiLoading} className="rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
